/**
 * Drives `<html data-layout="desktop|portrait|landscape">` (design `docs/design/mobile-layout.md`
 * §2–§3). Re-resolves the layout on every viewport change — `resize`, `orientationchange`,
 * `visualViewport` resize and the `(orientation: portrait)` / `(pointer: coarse)` media
 * queries — and on every settings change. Also sets `data-pointer="coarse|fine"` and
 * `data-mobile-controls="on|off"` (the CSS hook T3's overlay keys on).
 *
 * Rotation and the 3D view: `Game` already listens to `window` `resize` and re-sizes the
 * renderer + camera aspect from the container (`Game.onResize`). Some mobile browsers (iOS
 * Safari above all) fire `orientationchange` BEFORE the viewport has its new size and may
 * send no later `resize`, so after a rotation this re-dispatches one `resize` once the
 * viewport has settled — the canvas and the aspect then follow without touching Game.ts.
 *
 * The environment is injected so the controller runs under `node --test`.
 */

import { applyNativeOrientationLock, browserLockPorts } from './orientationLock.js';
import type { OrientationLockPorts } from './orientationLock.js';
import { resolveLayout } from './layoutMode.js';
import type { LayoutMode } from './layoutMode.js';
import { getLayoutPrefs, subscribeLayoutPrefs } from './layoutPrefs.js';
import type { LayoutPrefs } from './layoutPrefs.js';

export interface MediaQueryPort {
  matches: boolean;
  addEventListener?: (type: 'change', cb: () => void) => void;
  removeEventListener?: (type: 'change', cb: () => void) => void;
}

export interface LayoutEnv {
  innerWidth: () => number;
  innerHeight: () => number;
  matchMedia: (q: string) => MediaQueryPort;
  addEventListener: (type: string, cb: () => void, opts?: { once?: boolean }) => void;
  removeEventListener: (type: string, cb: () => void) => void;
  dispatchResize: () => void;
  /** `window.visualViewport`, when present. */
  visualViewport?: {
    addEventListener: (type: 'resize', cb: () => void) => void;
    removeEventListener: (type: 'resize', cb: () => void) => void;
  } | null;
  root: { dataset: Record<string, string | undefined> };
  /** setTimeout. */
  later: (cb: () => void, ms: number) => unknown;
  lockPorts: () => OrientationLockPorts;
}

/** How long after `orientationchange` the viewport is trusted to have its new size. */
export const ROTATE_SETTLE_MS = 250;

type ModeListener = (mode: LayoutMode) => void;

let currentMode: LayoutMode = 'desktop';
const modeListeners = new Set<ModeListener>();

/** The layout drawing now. */
export function getLayoutMode(): LayoutMode {
  return currentMode;
}

/** Subscribe to layout changes; returns the unsubscribe. */
export function subscribeLayoutMode(listener: ModeListener): () => void {
  modeListeners.add(listener);
  return () => {
    modeListeners.delete(listener);
  };
}

function setMode(mode: LayoutMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  for (const l of [...modeListeners]) l(mode);
}

/**
 * Install the controller. Returns an uninstall (tests, HMR). Idempotent in effect: a second
 * install just re-applies the same attributes.
 */
export function installLayoutController(env: LayoutEnv = browserLayoutEnv()): () => void {
  const portraitMq = env.matchMedia('(orientation: portrait)');
  const coarseMq = env.matchMedia('(pointer: coarse)');

  const apply = (prefs: LayoutPrefs = getLayoutPrefs()): LayoutMode => {
    const coarse = coarseMq.matches;
    const mode = resolveLayout({
      viewportWidth: env.innerWidth(),
      viewportHeight: env.innerHeight(),
      coarsePointer: coarse,
      orientationPref: prefs.orientation,
    });
    env.root.dataset.layout = mode;
    env.root.dataset.pointer = coarse ? 'coarse' : 'fine';
    env.root.dataset.mobileControls = prefs.mobileControls ? 'on' : 'off';
    env.root.dataset.orientationLock = prefs.orientation;
    setMode(mode);
    return mode;
  };

  const onViewport = (): void => {
    apply();
  };
  // After a rotation: re-resolve now, then once more when the viewport has settled, and
  // hand Game one `resize` so the canvas + camera aspect follow the new shape.
  const onRotate = (): void => {
    apply();
    env.later(() => {
      apply();
      env.dispatchResize();
    }, ROTATE_SETTLE_MS);
  };

  // A saved lock cannot request fullscreen at load (no gesture): try on the first tap.
  let gestureArmed = false;
  const onFirstGesture = (): void => {
    gestureArmed = false;
    void applyNativeOrientationLock(getLayoutPrefs().orientation, env.lockPorts(), true);
  };
  const armGesture = (prefs: LayoutPrefs): void => {
    if (prefs.orientation === 'auto' || !coarseMq.matches || gestureArmed) return;
    gestureArmed = true;
    env.addEventListener('pointerup', onFirstGesture, { once: true });
  };

  env.addEventListener('resize', onViewport);
  env.addEventListener('orientationchange', onRotate);
  env.visualViewport?.addEventListener('resize', onViewport);
  portraitMq.addEventListener?.('change', onRotate);
  coarseMq.addEventListener?.('change', onViewport);
  const unsubPrefs = subscribeLayoutPrefs((prefs) => {
    apply(prefs);
  });

  const prefs = getLayoutPrefs();
  apply(prefs);
  armGesture(prefs);

  return () => {
    env.removeEventListener('resize', onViewport);
    env.removeEventListener('orientationchange', onRotate);
    env.removeEventListener('pointerup', onFirstGesture);
    env.visualViewport?.removeEventListener('resize', onViewport);
    portraitMq.removeEventListener?.('change', onRotate);
    coarseMq.removeEventListener?.('change', onViewport);
    unsubPrefs();
  };
}

/** The live browser environment. */
export function browserLayoutEnv(): LayoutEnv {
  const noMq: MediaQueryPort = { matches: false };
  return {
    innerWidth: () => window.innerWidth,
    innerHeight: () => window.innerHeight,
    matchMedia: (q) => (typeof window.matchMedia === 'function' ? window.matchMedia(q) : noMq),
    addEventListener: (t, cb, opts) => window.addEventListener(t, cb, opts),
    removeEventListener: (t, cb) => window.removeEventListener(t, cb),
    dispatchResize: () => window.dispatchEvent(new Event('resize')),
    visualViewport: window.visualViewport ?? null,
    root: document.documentElement,
    later: (cb, ms) => window.setTimeout(cb, ms),
    lockPorts: browserLockPorts,
  };
}

/** Boot hook for main.tsx: never throws (a missing API leaves the desktop layout). */
export function installBrowserLayoutController(): void {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    installLayoutController();
  } catch {
    // The desktop layout stands.
  }
}
